import { ComponentIds, Entity, HierarchyUtils, ProductComponent } from '@inter-ikea-core/core';
import { Product, ProductDesign } from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import { useScreenshot } from 'contexts/screenshot';
import { useAppDispatch } from 'ducks/hooks';
import { getAgeGroup, showInfoMessage } from 'ducks/layout';
import { getMarketProducts } from 'ducks/products';
import { changeFrameColor } from 'rangeConfig/shared/utils/colorUtils/changeFrameColor';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Button, { ButtonTypeEnum } from 'ui/Button/Button';
import { getQuery, getRange } from 'utils/queryUtils';
import GenerateAssemblyModal from './GenerateAssemblyModal';
import ThumbnailPreviewModal from './ThumbnailPreviewModal';
import { CatalogData, generateCatalogData, generateTemplateData } from './utils';

export type AssemblyType = 'interior-suggestion' | 'front-layout';

export const ASSEMBLY_CATALOG_DATA = 'ASSEMBLY_CATALOG_DATA';
export const ASSEMBLY_TEMPLATE_DATA = 'ASSEMBLY_TEMPLATE_DATA';
const assemblyType: AssemblyType =
  getQuery().get('generateInteriorAssembly') === 'true' ? 'interior-suggestion' : 'front-layout';
const range = getRange();

const GenerateAssembly = () => {
  const coreApi = useCore();
  const products = useSelector(getMarketProducts);
  const dispatch = useAppDispatch();
  const screenshotTool = useScreenshot();
  const { captureInteriorSuggestion } = screenshotTool;
  const [images, setImages] = useState<{ name: string; url: string; error: boolean }[]>([]);
  const [thumbnailModalVisible, setThumbnailModalVisible] = useState(false);
  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [frameEntity, setFrameEntity] = useState<Entity | null>(null);
  const [generateQueue, setGenerateQueue] = useState<Product[]>([]);
  const [replaceError, setReplaceError] = useState(false);
  const [assemblyId, setAssemblyId] = useState<string>('');
  const currentAgeGroup = useSelector(getAgeGroup);
  const [applicableFrameTypesList, setApplicableFrameTypes] = useState<string[]>([]);
  const [privateimagePrefix, setimagePrefix] = useState<string>('');

  const handleNewCatalogSave = (catalogData: any) => {
    localStorage.setItem(ASSEMBLY_CATALOG_DATA, JSON.stringify([catalogData]));
  };

  const handleExistingCatalogSave = (catalogData: any) => {
    const existingCatalogData = localStorage.getItem(ASSEMBLY_CATALOG_DATA);

    if (!catalogData) return;

    if (existingCatalogData) {
      const parsedCatalogArray: CatalogData[] = JSON.parse(existingCatalogData);

      if (
        !parsedCatalogArray.some(existingCatalogData => existingCatalogData.id === catalogData?.id)
      ) {
        parsedCatalogArray.push(catalogData);
      }

      localStorage.setItem(ASSEMBLY_CATALOG_DATA, JSON.stringify(parsedCatalogArray));
    }
  };

  const handleNewTemplateSave = (templateData: any) => {
    localStorage.setItem(ASSEMBLY_TEMPLATE_DATA, JSON.stringify([templateData]));
  };

  const handleExistingTemplateSave = (templateData: any) => {
    const existingTemplateData = localStorage.getItem(ASSEMBLY_TEMPLATE_DATA);

    if (!templateData) return;

    if (existingTemplateData) {
      const parsedTemplateData: any[] = JSON.parse(existingTemplateData);

      // Only push if tempalte is not in list
      if (
        !parsedTemplateData.some((existingTemplate: { templateId: string }) => {
          return existingTemplate.templateId === templateData.templateId;
        })
      ) {
        parsedTemplateData.push(templateData);
        localStorage.setItem(ASSEMBLY_TEMPLATE_DATA, JSON.stringify(parsedTemplateData));
      }
    }
  };

  const handleSave = (catalogData: any, templateData: any) => {
    const catalogDataFromStorage = localStorage.getItem(ASSEMBLY_CATALOG_DATA);
    const templateDataFromStorage = localStorage.getItem(ASSEMBLY_TEMPLATE_DATA);

    if (!catalogDataFromStorage) handleNewCatalogSave(catalogData);
    else handleExistingCatalogSave(catalogData);

    if (!templateDataFromStorage) handleNewTemplateSave(templateData);
    else handleExistingTemplateSave(templateData);
    setThumbnailModalVisible(false);
  };

  const frames = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
  const onClickGenerate = async () => {
    const frame = frames.length === 1 ? frames[0] : coreApi.entities.findSelectedEntity();

    if (!frame || !frame.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) {
      return dispatch(
        showInfoMessage({
          message: 'Only have one frame in scene or select frame to generate assembly for'
        })
      );
    }
    if (HierarchyUtils.children(frame).length === 0) {
      return dispatch(showInfoMessage({ message: 'Can´t generate assembly for empty frame' }));
    }
    setFrameEntity(frame);
    setGenerateModalVisible(true);
  };

  const onGenerate = async (applicableFrameTypes?: string[], imagePrefix?: string) => {
    setGenerateModalVisible(false);
    if (!frameEntity) {
      return;
    }
    const catalogData = generateCatalogData(
      assemblyId,
      frameEntity,
      range,
      assemblyType,
      currentAgeGroup,
      applicableFrameTypes,
      imagePrefix
    );
    const templateData = generateTemplateData(frameEntity, catalogData, range, assemblyType);
    // eslint-disable-next-line no-console
    console.log('Catalog data', JSON.stringify(catalogData));
    // eslint-disable-next-line no-console
    console.log('Tempate data', JSON.stringify(templateData));

    coreApi.scene?.hideMeasurementLines();

    // If we failed to replace some interiors in the  previous
    // generation, we don't save
    if (!replaceError) {
      handleSave(catalogData, templateData);
    }

    if (assemblyType === 'interior-suggestion') {
      const image = await captureInteriorSuggestion(frameEntity);
      const imageName = catalogData.meta.image.slice(
        catalogData.meta.image.lastIndexOf('/') + 1,
        catalogData.meta.image.lastIndexOf('.')
      );
      setImages([...images, { name: imageName, url: image, error: replaceError }]);
      setThumbnailModalVisible(true);
    }

    const currentProduct = (frameEntity.getComponent(
      ComponentIds.PRODUCT_COMPONENT_ID
    ) as ProductComponent).productRef;

    const newQueue = generateQueue.filter(q => q.coreProductId !== currentProduct);
    const nextProduct = newQueue[0];
    if (newQueue.length > 0 && nextProduct) {
      const error = changeFrameColor(coreApi, [frameEntity], nextProduct.design, products);
      setReplaceError(error);

      const placedEntity = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
      if (!placedEntity[0]) {
        return;
      }

      const newEntity = coreApi.products.replaceEntityWithHierarchy(
        placedEntity[0].id,
        nextProduct.coreProductId,
        true
      );

      setFrameEntity(newEntity);
      setGenerateQueue(newQueue);
    }
  };

  const onGenerateAll = async (applicableFrameTypes?: string[], imagePrefix?: string) => {
    if (!frameEntity) {
      return;
    }

    if (applicableFrameTypes) {
      setApplicableFrameTypes(applicableFrameTypes);
    }

    if (imagePrefix) {
      setimagePrefix(imagePrefix);
    }

    const designsToGenerate = [
      ProductDesign.WHITE,
      ProductDesign.WHITE_STAINED_OAK,
      ProductDesign.BEIGE,
      ProductDesign.DARK_GREY
    ];

    const frameProductId = (frameEntity?.getComponentUnsafe(
      ComponentIds.PRODUCT_COMPONENT_ID
    ) as ProductComponent).productRef;
    const frameProduct = products.find(p => p.coreProductId === frameProductId);

    if (!frameProduct) {
      return;
    }
    const similarFrames = products.filter(
      p =>
        p.type === frameProduct.type &&
        p.dimensions.depth === frameProduct.dimensions.depth &&
        p.dimensions.height === frameProduct.dimensions.height &&
        p.dimensions.width === frameProduct.dimensions.width &&
        designsToGenerate.includes(p.design)
    );

    setGenerateQueue(similarFrames);
  };

  const clearStorage = () => {
    setImages([]);
    const acceptPrompt = prompt(
      'This will delete all current unsaved catalog data and template data, are you sure you want to delete it?',
      'Yes'
    );

    if (acceptPrompt) {
      localStorage.removeItem(ASSEMBLY_CATALOG_DATA);
      localStorage.removeItem(ASSEMBLY_TEMPLATE_DATA);
    }
  };

  const createLinkAndDownload = (data: string, fileName: string) => {
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json'
      })
    );

    const fakeDownloadLink = document.createElement('a');
    fakeDownloadLink.setAttribute('href', url);
    fakeDownloadLink.setAttribute('download', `${fileName}.json`);
    document.body.appendChild(fakeDownloadLink);
    fakeDownloadLink.click();
    document.body.removeChild(fakeDownloadLink);
  };

  const downloadJson = (includedEntries?: string[]) => {
    const catalog = localStorage.getItem(ASSEMBLY_CATALOG_DATA);
    const template = localStorage.getItem(ASSEMBLY_TEMPLATE_DATA);

    if (catalog) {
      const parsedCatalog = JSON.parse(catalog);
      const filteredCatalog = includedEntries
        ? parsedCatalog.filter((entry: any) => includedEntries.includes(entry.id))
        : parsedCatalog;
      createLinkAndDownload(filteredCatalog, 'catalog');
    }

    if (template) {
      const parsedTemplates = JSON.parse(template);
      parsedTemplates.forEach((template: any) =>
        createLinkAndDownload(template, template.templateId)
      );
    }
  };

  useEffect(() => {
    const onLoaded = async () => {
      if (generateQueue.length > 0) {
        await setTimeout(async () => {
          await onGenerate(applicableFrameTypesList, privateimagePrefix);
        }, 2000);
      }
    };
    onLoaded();

    return coreApi.subscribe('allModelsLoaded', onLoaded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coreApi, generateQueue]);

  const onDownloadFromThumbPreview = (includedEntries: string[]) => {
    downloadJson(includedEntries);
  };

  const onDownload = () => {
    if (assemblyType === 'interior-suggestion') {
      setThumbnailModalVisible(true);
    } else {
      downloadJson();
    }
  };

  const onInput = (text: string) => {
    setAssemblyId(text);
  };

  return (
    <div style={{ pointerEvents: 'auto', display: 'flex' }}>
      <Button
        mr="xs"
        onClick={onClickGenerate}
        size={'small'}
        type={ButtonTypeEnum.emphasised}
        text="Generate"
      />
      <Button
        mr="xs"
        size={'small'}
        type={ButtonTypeEnum.primary}
        text="Download"
        onClick={onDownload}
      />
      <Button onClick={clearStorage} size={'small'} type={ButtonTypeEnum.primary} text="Clear" />
      <GenerateAssemblyModal
        visible={generateModalVisible}
        onCancel={() => setGenerateModalVisible(false)}
        onGenerate={onGenerate}
        onGenerateAll={onGenerateAll}
        onInput={onInput}
        assemblyId={assemblyId}
      />
      {images.length > 0 && (
        <ThumbnailPreviewModal
          images={images}
          onDownload={onDownloadFromThumbPreview}
          visible={thumbnailModalVisible}
          hideModal={() => setThumbnailModalVisible(false)}
        />
      )}
    </div>
  );
};

export default GenerateAssembly;
